import { Reducer } from 'redux';
import * as Dtos from '../dtos/Tmd.dtos';
import { update } from '../helpers/immutabilityHelper';

import {
    ACTION_PROJECTEDACCRUAL_CLEAR,
    ACTION_PROJECTEDACCRUAL_LOAD_BY_ID,
    ACTION_PROJECTEDACCRUAL_LOAD_BY_TRIAL_ID,
    ACTION_PROJECTEDACCRUAL_LOAD_FAILURE,
    ACTION_PROJECTEDACCRUAL_LOAD_SUCCESS,

    ACTION_PROJECTEDACCRUAL_SAVE,
    ACTION_PROJECTEDACCRUAL_SAVE_FAILURE,
    ACTION_PROJECTEDACCRUAL_SAVE_SUCCESS,

    ACTION_PROJECTEDACCRUAL_UPDATE,
    ACTION_PROJECTEDACCRUAL_UPDATE_FAILURE,
    ACTION_PROJECTEDACCRUAL_UPDATE_SUCCESS,
    ProjectedAccrualAction
} from '../actions/projectedAccrual';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState } from "./index";

export interface IProjectedAccrualState {
    data: Dtos.ProjectedAccrual | Dtos.ProjectedAccrual[];
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
}

const initialState: IProjectedAccrualState = {
    data: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState
};


function loading(state: IProjectedAccrualState, _isLoading: boolean): IProjectedAccrualState {
    const newState: IProjectedAccrualState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IProjectedAccrualState,
    data: Dtos.ProjectedAccrual | Dtos.ProjectedAccrual[],
    responseStatus: Dtos.ResponseStatus
): IProjectedAccrualState {
    const newState: IProjectedAccrualState = update(state, {
        data: {
            $set: data
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
    });

    return newState;
}

function loadFailure(state: IProjectedAccrualState, responseStatus: Dtos.ResponseStatus): IProjectedAccrualState {
    const newState: IProjectedAccrualState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IProjectedAccrualState, propertiesToClear?: string[]): IProjectedAccrualState {

    if (!propertiesToClear) {
        const newState: IProjectedAccrualState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IProjectedAccrualState, _isSaving: boolean): IProjectedAccrualState {
    const newState: IProjectedAccrualState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IProjectedAccrualState,
    data: Dtos.ProjectedAccrual | Dtos.ProjectedAccrual[],
    responseStatus: Dtos.ResponseStatus
): IProjectedAccrualState {
    const newState: IProjectedAccrualState = update(state, {
        data: {
            $set: data
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        }
    });

    return newState;
}

function saveFailure(state: IProjectedAccrualState, responseStatus: Dtos.ResponseStatus): IProjectedAccrualState {
    const newState: IProjectedAccrualState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        }
    });

    return newState;
}

function updating(state: IProjectedAccrualState, _isUpdating: boolean): IProjectedAccrualState {
    const newState: IProjectedAccrualState = update(state, {
        updateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function updateSuccess(
    state: IProjectedAccrualState,
    data: Dtos.ProjectedAccrual | Dtos.ProjectedAccrual[],
    responseStatus: Dtos.ResponseStatus
): IProjectedAccrualState {
    const newState: IProjectedAccrualState = update(state, {
        data: {
            $set: data
        },
        updateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        }
    });

    return newState;
}

function updateFailure(state: IProjectedAccrualState, responseStatus: Dtos.ResponseStatus): IProjectedAccrualState {
    const newState: IProjectedAccrualState = update(state, {
        updateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        }
    });

    return newState;
}

const ProjectedAccrualReducer: Reducer<IProjectedAccrualState> = (state: IProjectedAccrualState = initialState, action: ProjectedAccrualAction) => {
    switch (action.type) {
        case ACTION_PROJECTEDACCRUAL_LOAD_BY_TRIAL_ID:
        case ACTION_PROJECTEDACCRUAL_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_PROJECTEDACCRUAL_LOAD_SUCCESS:
            return loadSuccess(state, action.data, action.responseStatus);
        case ACTION_PROJECTEDACCRUAL_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_PROJECTEDACCRUAL_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_PROJECTEDACCRUAL_SAVE:
            return saving(state, true);
        case ACTION_PROJECTEDACCRUAL_SAVE_SUCCESS:
            return saveSuccess(state, action.data, action.responseStatus);
        case ACTION_PROJECTEDACCRUAL_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_PROJECTEDACCRUAL_UPDATE:
            return updating(state, true);
        case ACTION_PROJECTEDACCRUAL_UPDATE_SUCCESS:
            return updateSuccess(state, action.data, action.responseStatus);
        case ACTION_PROJECTEDACCRUAL_UPDATE_FAILURE:
            return updateFailure(state, action.responseStatus);
    }

    return state;
}

export default ProjectedAccrualReducer;